/**
 * Shared constants: the environment flags, the backend addresses, the protocol
 * version and extension level, the plane types, the team tints, and the zoom,
 * minimap and map numbers more than one module reads. Anything one module owns
 * lives there.
 */

/**
 * Development mode detection
 *
 * True on a 127.0.0.1 origin (legacy's check) or with a `?dev` query parameter
 * (case-insensitive — `?dev`, `?DEV`, `?DeV` all match). Plain localhost isn't
 * dev mode, so it behaves like the live site.
 */
export const DEVELOPMENT =
  /^http:\/\/127\.0\.0\.1:[0-9]{1,5}\/?$/.test(window.origin) ||
  [...new URLSearchParams(window.location.search).keys()].some((k) => k.toLowerCase() === 'dev');

/**
 * Storage reset request
 *
 * True if a `?reset` query parameter is present (case-insensitive — `?reset`,
 * `?RESET`, `?Reset` all match). Triggers a one-time `localStorage` wipe at
 * startup for a clean slate (settings, keybinds, saved name, auth, caches),
 * after which the param is stripped from the URL so a refresh doesn't keep
 * wiping. Useful for reproducing first-run state and clearing corrupt settings.
 */
export const RESET_REQUESTED = [...new URLSearchParams(window.location.search).keys()].some(
  (k) => k.toLowerCase() === 'reset',
);

/** The ab-protocol version this client speaks; the backend's games list names the current one. */
export const PROTOCOL_VERSION = 5;

/**
 * The highest protocol extension level this client speaks, asked for on the socket URL
 * (`?ext=2`): a server that doesn't speak it ignores the query, and ours answer with the
 * connection's level as `ext` in the LOGIN packet's configuration. 1 is the stock protocol,
 * which is all a community server speaks. Level 2 enables nothing yet.
 */
export const EXTENSION_LEVEL = 2;

/** The data backend: the games list, account settings, and telemetry. */
export const BACKEND_HOST = 'data.airmash.rocks';

/** The login service: the OAuth popup opens here and posts the tokens back to this window. */
export const LOGIN_ORIGIN = 'https://login.airmash.rocks';

/**
 * Aircraft type IDs, under the names the client uses: the values of the protocol's PLANE_TYPES, which the packet
 * types carry. The two convert either way without a cast.
 */
export enum PlaneType {
  Predator = 1,
  Goliath = 2,
  Mohawk = 3,
  Tornado = 4,
  Prowler = 5,
}

// CTF team tints by team id: the hitshapes theme's planes and missiles (as in the SWAM original), their
// thrusters, and vanilla's missiles
export const HITSHAPES_TEAM_1_COLOR = 0x5dd4e9; // Cyan/blue
export const HITSHAPES_TEAM_2_COLOR = 0xf9557a; // Red/pink
export const HITSHAPES_TEAM_1_THRUSTER = 0xa7e8fa; // Light cyan
export const HITSHAPES_TEAM_2_THRUSTER = 0xfab7b7; // Light pink
export const MISSILE_TEAM_1_COLOR = 0x3232fa; // Blue
export const MISSILE_TEAM_2_COLOR = 0xea4242; // Red

/**
 * Scaling factor (zoom) bounds. Drive the view radius around the player —
 * smaller = more zoomed in, larger = more zoomed out. Shared by the
 * keyboard zoom keybinds, the game-side capScalingFactor clamp, the
 * initial config in Config.ts, the in-game zoom-slider, and
 * the menu-drawer's zoom range-slider. Single source of truth.
 *
 * The server has its own cap, usually stricter, sent in the login reply as
 * `serverConfiguration.sf` and kept in `session.serverConfig.sf`; the client
 * does not clamp to it yet.
 */
export const SCALE_MIN = 800;
export const SCALE_MAX = 7000;
export const SCALE_DEFAULT = 2500;

/** The alternate zoom level, the one the ALTZOOM key switches to, until the
 *  player sets their own. Also what its menu slider resets to. */
export const ALT_SCALE_DEFAULT = 4000;

/** Minimap size multiplier until the player resizes it, and what a reset
 *  (double-click, or the menu slider) returns to. */
export const MINIMAP_SCALE_DEFAULT = 1.0;

/** The minimap zoom's range. */
export const MINIMAP_SCALE_MIN = 0.75;
export const MINIMAP_SCALE_MAX = 3.0;

/**
 * A visual viewport scale at or below this is no pinch zoom, only float noise.
 * Above it, ZoomGuard snaps a phone's stuck pinch back, and UI.ts keeps the
 * renderer at the layout viewport's size rather than rebuilding it for a
 * gesture about to be undone.
 */
export const PINCH_SCALE_TOLERANCE = 1.02;

/** World map dimensions (server-defined, fixed for all game modes) */
export const MAP_WIDTH = 32768;
export const MAP_HEIGHT = 16384;
export const MAP_CENTER_X = MAP_WIDTH / 2;
export const MAP_CENTER_Y = MAP_HEIGHT / 2;

/** Position-clamp inset from the true map edge — keeps plane sprites from
 *  clipping into the edge border. Used by Player.update boundary clamping. */
export const MAP_BOUNDARY_INSET = 32;

/** Full circle in radians (2π ≈ 6.28) */
export const TWO_PI = Math.PI * 2;
export const HALF_PI = Math.PI * 0.5;
export const MS_PER_FRAME_60FPS = 1000 / 60;

/** How far the canvas extends past the viewport on each axis, in pixels, so sprites at the edge are not clipped. */
export const OVERDRAW = 256;

/** The shadow layer's divisor: shadow sprites are placed and scaled in a space this many times smaller. */
export const SHADOW_SCALING = 2;

/** How many chat lines the game keeps before dropping the oldest. */
export const MAX_CHAT_LINES = 50;
